import { makeDraggable } from '../ui/draggablePanel.js';
import { closeTooltipFade, openTooltipFade } from '../ui/tooltipFade.js';
import type { GameScene } from '../game/GameScene.js';
import type { CameraFollowMode } from '../circuits/circuit3dPreview.js';
import { CAMERA_MODE_OPTIONS } from './cameraModeLabels.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { getCurrentUser } from '../auth/session.js';

import { formatTurnHistoryRollMarkup, turnHistoryRowsInRollOrder } from './turnHistoryRoll.js';
import { buildRaceResultsTableRows, formatDiceRollsLabel } from './raceResultsMarkup.js';

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function rankStatusBadges(scene: GameScene, playerIndex: number): string {
  const parts: string[] = [];
  if (scene.isPlayerJokerLapUsed(playerIndex)) {
    parts.push(
      `<span class="race-status-badge race-status-badge--joker" title="Tour joker pris" aria-label="Tour joker pris">${JOKER_STAR_ICON}</span>`
    );
  }
  if (scene.isPlayerFinishedRace(playerIndex)) {
    parts.push(
      `<span class="race-status-badge race-status-badge--finished" title="Course terminée" aria-label="Course terminée">${FINISH_CHECK_ICON}</span>`
    );
  }
  if (parts.length === 0) return '';
  return `<span class="play-race-rank-badges">${parts.join('')}</span>`;
}

const FINISH_CHECK_ICON =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5 6.5 11.5 12.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const JOKER_STAR_ICON =
  '<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1.75 9.55 5.9l4.35.63-3.15 3.07.74 4.33L8 11.68l-3.89 2.05.74-4.33L1.7 6.53l4.35-.63L8 1.75z"/></svg>';

/**
 * Composants flottants au-dessus du canvas (classement, dé, bannière fin).
 */
export class RaceHud {
  readonly layer: HTMLDivElement;
  readonly floatStack: HTMLDivElement;
  readonly rankingPanel: HTMLDivElement;
  readonly rankingLap: HTMLDivElement;
  readonly rankingList: HTMLDivElement;
  readonly rankingRoundStart: HTMLDivElement;
  readonly rankingRollOrder: HTMLDivElement;
  readonly rankingTooltip: HTMLDivElement;
  readonly historyPanel: HTMLDivElement;
  readonly historyTurnLabel: HTMLSpanElement;
  readonly historyPrevBtn: HTMLButtonElement;
  readonly historyNextBtn: HTMLButtonElement;
  readonly historyList: HTMLDivElement;
  readonly diceDockSlot: HTMLDivElement;
  readonly mobileBar: HTMLDivElement;
  readonly mobileMenuBtn: HTMLButtonElement;
  readonly mobileDrawerTabRanking: HTMLButtonElement;
  readonly mobileDrawerTabHistory: HTMLButtonElement;
  readonly viewSwitch: HTMLDivElement;
  readonly viewSwitchToggle: HTMLButtonElement;
  readonly viewControlsWrap: HTMLDivElement;
  readonly cameraModeSwitch: HTMLDivElement;
  readonly cameraModeButtons: Record<CameraFollowMode, HTMLButtonElement>;
  readonly finishedSlot: HTMLDivElement;
  readonly resultsOverlay: HTMLDivElement;
  readonly resultsTableBody: HTMLTableSectionElement;
  readonly resultsTitle: HTMLHeadingElement;
  readonly resultsSubtitle: HTMLParagraphElement;
  readonly resultsActions: HTMLDivElement;

  private collapsed = false;
  private mobileDrawerOpen = false;

  constructor(gameMain: HTMLElement) {
    this.layer = document.createElement('div');
    this.layer.className = 'play-race-hud';
    this.layer.hidden = true;

    this.finishedSlot = document.createElement('div');
    this.finishedSlot.className = 'play-race-hud-finished-slot';

    this.rankingPanel = document.createElement('div');
    this.rankingPanel.className = 'play-race-float play-race-ranking';
    this.rankingPanel.dataset.dragId = 'race-ranking';
    const rankHead = document.createElement('div');
    rankHead.className = 'play-race-float-head';
    rankHead.innerHTML = `
      <span class="play-race-float-drag" title="Déplacer">⠿</span>
      <span class="play-race-float-title-wrap">
        <span class="play-race-float-title">Classement</span>
        <span class="play-race-float-subtitle">en piste · live</span>
      </span>`;
    const btnCollapse = document.createElement('button');
    btnCollapse.type = 'button';
    btnCollapse.className = 'play-race-float-collapse btn btn-ghost btn-sm';
    btnCollapse.title = 'Réduire';
    btnCollapse.textContent = '−';
    btnCollapse.addEventListener('click', () => this.toggleRankingCollapsed());
    rankHead.appendChild(btnCollapse);
    this.rankingLap = document.createElement('div');
    this.rankingLap.className = 'play-race-ranking-lap';
    this.rankingLap.hidden = true;
    this.rankingList = document.createElement('div');
    this.rankingList.className = 'play-race-ranking-list';
    this.rankingRoundStart = document.createElement('div');
    this.rankingRoundStart.className = 'play-race-ranking-round-start';
    this.rankingRoundStart.hidden = true;
    this.rankingRollOrder = document.createElement('div');
    this.rankingRollOrder.className = 'play-race-ranking-roll-order';
    this.rankingRollOrder.hidden = true;
    this.rankingPanel.append(
      rankHead,
      this.rankingLap,
      this.rankingList,
      this.rankingRoundStart,
      this.rankingRollOrder
    );
    makeDraggable(
      this.rankingPanel,
      rankHead.querySelector('.play-race-float-drag') as HTMLElement
    );

    this.rankingTooltip = document.createElement('div');
    this.rankingTooltip.className = 'race-profile-tooltip play-race-ranking-tooltip';
    this.rankingTooltip.hidden = true;

    this.historyPanel = document.createElement('div');
    this.historyPanel.className = 'play-race-float play-race-history';
    this.historyPanel.dataset.dragId = 'race-history';
    const historyHead = document.createElement('div');
    historyHead.className = 'play-race-float-head';
    historyHead.innerHTML = `
      <span class="play-race-float-drag" title="Déplacer">⠿</span>
      <span class="play-race-float-title-wrap">
        <span class="play-race-float-title">Historique</span>
        <span class="play-race-float-subtitle">ordre des lancers</span>
      </span>`;
    const historyNav = document.createElement('div');
    historyNav.className = 'play-race-history-nav';
    this.historyPrevBtn = document.createElement('button');
    this.historyPrevBtn.type = 'button';
    this.historyPrevBtn.className = 'btn btn-ghost btn-sm play-race-history-nav-btn';
    this.historyPrevBtn.textContent = '←';
    this.historyPrevBtn.title = 'Tour précédent';
    this.historyTurnLabel = document.createElement('span');
    this.historyTurnLabel.className = 'play-race-history-turn';
    this.historyNextBtn = document.createElement('button');
    this.historyNextBtn.type = 'button';
    this.historyNextBtn.className = 'btn btn-ghost btn-sm play-race-history-nav-btn';
    this.historyNextBtn.textContent = '→';
    this.historyNextBtn.title = 'Tour suivant';
    historyNav.append(this.historyPrevBtn, this.historyTurnLabel, this.historyNextBtn);
    historyHead.appendChild(historyNav);
    this.historyList = document.createElement('div');
    this.historyList.className = 'play-race-history-list';
    this.historyPanel.append(historyHead, this.historyList);
    makeDraggable(
      this.historyPanel,
      historyHead.querySelector('.play-race-float-drag') as HTMLElement
    );

    // Onglets Classement/Historique du tiroir mobile — ignorés en desktop
    // (`display: none` par défaut, activés uniquement dans le media query
    // mobile de play-race-hud.css). Ajoutés en tête de `floatStack` plutôt
    // que dans un nouveau wrapper pour ne rien changer au `display: contents`
    // par défaut dont dépend le positionnement flottant desktop des panneaux.
    const mobileDrawerTabs = document.createElement('div');
    mobileDrawerTabs.className = 'play-race-drawer-tabs';
    this.mobileDrawerTabRanking = document.createElement('button');
    this.mobileDrawerTabRanking.type = 'button';
    this.mobileDrawerTabRanking.className = 'play-race-drawer-tab is-active';
    this.mobileDrawerTabRanking.textContent = 'Classement';
    this.mobileDrawerTabHistory = document.createElement('button');
    this.mobileDrawerTabHistory.type = 'button';
    this.mobileDrawerTabHistory.className = 'play-race-drawer-tab';
    this.mobileDrawerTabHistory.textContent = 'Historique des lancers';
    const setActiveTab = (tab: 'ranking' | 'history'): void => {
      this.floatStack.dataset.activeTab = tab;
      this.mobileDrawerTabRanking.classList.toggle('is-active', tab === 'ranking');
      this.mobileDrawerTabHistory.classList.toggle('is-active', tab === 'history');
    };
    this.mobileDrawerTabRanking.addEventListener('click', () => setActiveTab('ranking'));
    this.mobileDrawerTabHistory.addEventListener('click', () => setActiveTab('history'));
    mobileDrawerTabs.append(this.mobileDrawerTabRanking, this.mobileDrawerTabHistory);

    this.diceDockSlot = document.createElement('div');
    this.diceDockSlot.className = 'play-race-dice-dock';

    // Barre mobile — enrobe le dock du dé d'une poignée de glissement
    // (verticale uniquement, voir `makeDraggable(..., {axis:'y'})`) et d'un
    // bouton menu ouvrant le tiroir Classement/Historique ci-dessus. `display:
    // contents` par défaut (desktop) : le dock garde son positionnement
    // absolu centré habituel, cette barre n'existe visuellement qu'en mobile.
    this.mobileBar = document.createElement('div');
    this.mobileBar.className = 'play-race-mobile-bar';
    this.mobileBar.dataset.dragId = 'race-mobile-bar';
    const mobileBarDrag = document.createElement('span');
    mobileBarDrag.className = 'play-race-mobile-bar-drag';
    mobileBarDrag.title = 'Déplacer';
    mobileBarDrag.setAttribute('aria-hidden', 'true');
    this.mobileMenuBtn = document.createElement('button');
    this.mobileMenuBtn.type = 'button';
    this.mobileMenuBtn.className = 'play-race-mobile-menu-btn';
    this.mobileMenuBtn.setAttribute('aria-label', 'Classement et historique des lancers');
    this.mobileMenuBtn.setAttribute('aria-expanded', 'false');
    this.mobileMenuBtn.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    this.mobileMenuBtn.addEventListener('click', () => this.toggleMobileDrawer());
    this.mobileBar.append(mobileBarDrag, this.diceDockSlot, this.mobileMenuBtn);
    makeDraggable(this.mobileBar, mobileBarDrag, { axis: 'y' });

    this.viewSwitch = document.createElement('div');
    this.viewSwitch.className = 'play-race-view-switch';
    this.viewSwitch.innerHTML = `
      <span class="play-race-view-switch-label play-race-view-switch-label--2d">2D</span>
      <button
        type="button"
        class="play-race-view-switch-toggle"
        role="switch"
        aria-checked="false"
        aria-label="Basculer entre la vue 2D et la vue 3D (POC)"
      >
        <span class="play-race-view-switch-thumb"></span>
      </button>
      <span class="play-race-view-switch-label play-race-view-switch-label--3d">3D</span>`;
    this.viewSwitchToggle = this.viewSwitch.querySelector(
      '.play-race-view-switch-toggle'
    ) as HTMLButtonElement;

    // Choix de la voiture suivie par la caméra 3D — n'a de sens qu'en vue 3D
    // (voir `setViewMode` ci-dessous, qui le cache/montre avec le switch 2D/3D).
    // `OrbitControls` reste actif dans les deux modes suivi : seul le point visé
    // se déplace avec la voiture, l'utilisateur garde l'orbite/le zoom libres.
    this.cameraModeSwitch = document.createElement('div');
    this.cameraModeSwitch.className = 'play-race-camera-modes';
    this.cameraModeSwitch.hidden = true;
    this.cameraModeSwitch.setAttribute('role', 'radiogroup');
    this.cameraModeSwitch.setAttribute('aria-label', 'Mode caméra');
    const cameraModeButtons = {} as Record<CameraFollowMode, HTMLButtonElement>;
    for (const { mode, label } of CAMERA_MODE_OPTIONS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'play-race-camera-mode-btn';
      btn.dataset.cameraMode = mode;
      btn.textContent = label;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', mode === 'free' ? 'true' : 'false');
      btn.classList.toggle('is-active', mode === 'free');
      this.cameraModeSwitch.appendChild(btn);
      cameraModeButtons[mode] = btn;
    }
    this.cameraModeButtons = cameraModeButtons;

    this.viewControlsWrap = document.createElement('div');
    this.viewControlsWrap.className = 'play-race-view-controls';
    this.viewControlsWrap.append(this.viewSwitch, this.cameraModeSwitch);

    this.resultsOverlay = document.createElement('div');
    this.resultsOverlay.className = 'play-race-results-overlay';
    this.resultsOverlay.hidden = true;
    this.resultsOverlay.setAttribute('role', 'dialog');
    this.resultsOverlay.setAttribute('aria-modal', 'true');
    this.resultsOverlay.setAttribute('aria-label', 'Résultats de la course');

    const resultsCard = document.createElement('div');
    resultsCard.className = 'play-race-results-card';

    this.resultsTitle = document.createElement('h2');
    this.resultsTitle.className = 'play-race-results-title';
    this.resultsTitle.textContent = 'Course terminée';

    this.resultsSubtitle = document.createElement('p');
    this.resultsSubtitle.className = 'play-race-results-subtitle';

    const tableWrap = document.createElement('div');
    tableWrap.className = 'play-race-results-table-wrap';
    const table = document.createElement('table');
    table.className = 'play-race-results-table';
    table.innerHTML = `<thead>
      <tr>
        <th scope="col">Pos</th>
        <th scope="col">Pilote</th>
        <th scope="col" class="play-race-results-th--stat" title="Lancers de dé">🎲</th>
        <th scope="col" class="play-race-results-th--stat" title="Dépassements réussis">↑</th>
        <th scope="col" class="play-race-results-th--stat" title="Dépassements contrés">🛡</th>
      </tr>
    </thead>`;
    this.resultsTableBody = document.createElement('tbody');
    table.appendChild(this.resultsTableBody);
    tableWrap.appendChild(table);

    this.resultsActions = document.createElement('div');
    this.resultsActions.className = 'play-race-results-actions';

    resultsCard.append(this.resultsTitle, this.resultsSubtitle, tableWrap, this.resultsActions);
    this.resultsOverlay.appendChild(resultsCard);

    // `display: contents` sur desktop (voir play-race-hud.css) : le wrapper
    // n'a aucun effet sur la mise en page, chaque panneau garde son propre
    // positionnement absolu/glissable. Sur mobile, il devient le conteneur
    // scrollable de la pile Classement+Historique, séparé du dé et de la
    // bannière de fin qui doivent rester visibles quel que soit son contenu.
    this.floatStack = document.createElement('div');
    this.floatStack.className = 'play-race-float-stack';
    this.floatStack.dataset.activeTab = 'ranking';
    this.floatStack.append(mobileDrawerTabs, this.rankingPanel, this.historyPanel);

    this.layer.append(
      this.resultsOverlay,
      this.finishedSlot,
      this.floatStack,
      this.rankingTooltip,
      this.mobileBar
    );
    gameMain.appendChild(this.layer);
  }

  private toggleMobileDrawer(open = !this.mobileDrawerOpen): void {
    this.mobileDrawerOpen = open;
    this.floatStack.classList.toggle('is-mobile-drawer-open', open);
    this.mobileMenuBtn.classList.toggle('is-active', open);
    this.mobileMenuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  setVisible(visible: boolean): void {
    this.layer.hidden = !visible;
    if (visible) {
      document.getElementById('view-course')?.classList.add('play-race-active');
    } else {
      document.getElementById('view-course')?.classList.remove('play-race-active');
    }
  }

  mountDiceDock(content: HTMLElement): void {
    this.diceDockSlot.innerHTML = '';
    this.diceDockSlot.appendChild(content);
  }

  mountFinishedBanner(banner: HTMLElement): void {
    this.finishedSlot.innerHTML = '';
    this.finishedSlot.appendChild(banner);
  }

  showRaceResults(visible: boolean): void {
    this.resultsOverlay.hidden = !visible;
    this.finishedSlot.hidden = true;
    this.rankingPanel.hidden = visible;
    this.historyPanel.hidden = visible;
    this.mobileBar.hidden = visible;
    this.viewControlsWrap.hidden = visible;
    if (visible) this.toggleMobileDrawer(false);
  }

  /** Reflète l'état 2D/3D actuel sur le switch (position du curseur, libellé actif) —
   * le choix du mode caméra n'a de sens qu'en 3D, masqué sinon. */
  setViewMode(mode: '2d' | '3d'): void {
    const is3d = mode === '3d';
    this.viewSwitchToggle.setAttribute('aria-checked', is3d ? 'true' : 'false');
    this.viewSwitch.classList.toggle('is-3d', is3d);
    this.cameraModeSwitch.hidden = !is3d;
  }

  /** Reflète le mode caméra actif sur les boutons segmentés (voir `DiceUI`, qui
   * pilote les clics et répercute le mode sur `Circuit3dPreviewHandle`). */
  setCameraModeActive(mode: CameraFollowMode): void {
    for (const [m, btn] of Object.entries(this.cameraModeButtons) as [
      CameraFollowMode,
      HTMLButtonElement,
    ][]) {
      const active = m === mode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    }
  }

  updateRaceResults(scene: GameScene): void {
    const ranking = scene.getRanking();
    const diceRolls = scene.getPlayerTotalDiceRolls();
    const overtakesSucceeded = scene.getPlayerOvertakesCount();
    const overtakesCountered = scene.getPlayerOvertakesCounteredCount();
    const winnerIdx = scene.getWinnerIndex();
    const winnerName =
      winnerIdx >= 0
        ? labelOwnDriver(
            scene.getPlayerDisplayInfo(winnerIdx).driverName,
            scene.getPlayerDriverId(winnerIdx)
          )
        : '';

    this.resultsTitle.textContent = 'Course terminée';
    this.resultsSubtitle.textContent =
      winnerName.length > 0 ? `Vainqueur : ${winnerName}` : 'Classement final';

    const rows = ranking.map(({ playerIndex, rank }) => {
      const info = scene.getPlayerDisplayInfo(playerIndex);
      return {
        rank,
        driverName: labelOwnDriver(info.driverName, scene.getPlayerDriverId(playerIndex)),
        carName: info.carName,
        nationality: info.nationality,
        diceRolls: diceRolls[playerIndex] ?? 0,
        overtakesSucceeded: overtakesSucceeded[playerIndex] ?? 0,
        overtakesCountered: overtakesCountered[playerIndex] ?? 0,
      };
    });
    this.resultsTableBody.innerHTML = buildRaceResultsTableRows(rows);

    const totalRolls = rows.reduce((sum, row) => sum + row.diceRolls, 0);
    this.resultsSubtitle.title = `${formatDiceRollsLabel(totalRolls)} au total`;
  }

  setResultsActionsHtml(html: string): void {
    this.resultsActions.innerHTML = html;
  }

  updateRanking(scene: GameScene): void {
    if (scene.getRaceStarted()) {
      const { current, total } = scene.getRaceLapIndicator();
      this.rankingLap.textContent = `${current}/${total}`;
      this.rankingLap.hidden = false;
    } else {
      this.rankingLap.hidden = true;
    }

    const totalTurns = scene.getTurnHistoryTotal();
    const selectedTurn = scene.getSelectedHistoryTurn();
    const currentEntry = scene.getTurnHistoryEntry(selectedTurn);

    // Classement : live ou fin du tour sélectionné dans l’historique.
    const ranking = scene.getRankingForHudDisplay();
    const currentPlayer = scene.getCurrentPlayerIndex();
    const rollOrder =
      scene.getRaceStarted() && !scene.getRaceFinished() ? scene.getDiceRollOrderThisRound() : [];
    const atRoundStart = scene.isAtDiceRoundStart();

    const canTakeControl = getCurrentUser()?.role === 'admin' && scene.getIsMultiplayer();

    this.rankingList.innerHTML = ranking
      .map((r) => {
        const info = scene.getPlayerDisplayInfo(r.playerIndex);
        const badges = rankStatusBadges(scene, r.playerIndex);
        const flag = info.nationality
          ? `${nationalityFlagMarkup(info.nationality, { className: 'play-race-rank-flag' })} `
          : '';
        const isCurrent = r.playerIndex === currentPlayer;
        const rowClass = isCurrent
          ? 'play-race-rank-row play-race-rank-row--current'
          : 'play-race-rank-row';
        const takeoverBtn =
          canTakeControl && !scene.isPlayerSlotHuman(r.playerIndex)
            ? `<button type="button" class="btn btn-ghost btn-sm play-race-rank-takeover" data-take-control="${r.playerIndex}" title="Prendre le contrôle de ce pilote (IA)">🎮</button>`
            : '';
        return `<div class="${rowClass}" data-player-index="${r.playerIndex}" tabindex="0">
          <span class="play-race-rank-pos">${r.rank}.</span>
          <span class="play-race-rank-name"><span class="play-race-rank-name-text">${flag}${escapeHtml(labelOwnDriver(info.driverName, scene.getPlayerDriverId(r.playerIndex)))}</span>${badges}</span>
          ${takeoverBtn}
        </div>`;
      })
      .join('');

    this.rankingList.querySelectorAll<HTMLButtonElement>('[data-take-control]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(btn.dataset.takeControl ?? '-1', 10);
        if (idx >= 0) scene.adminTakeControlOfSlot(idx);
      });
    });

    if (rollOrder.length > 1 && !atRoundStart) {
      const parts = rollOrder.map((o) => {
        const name = scene.getPlayerDisplayInfo(o.playerIndex).driverName;
        const short = name.split(' ').pop() ?? name;
        const labeled = labelOwnDriver(short, scene.getPlayerDriverId(o.playerIndex));
        if (o.isCurrent)
          return `<strong class="play-race-roll-order-current">${escapeHtml(labeled)}</strong>`;
        if (o.hasRolledThisRound)
          return `<span class="play-race-roll-order-done">${escapeHtml(labeled)}</span>`;
        return escapeHtml(labeled);
      });
      this.rankingRollOrder.innerHTML = `<span class="play-race-roll-order-label">Prochains lancers</span> ${parts.join(' → ')}`;
      this.rankingRollOrder.hidden = false;

      const atDepart = scene.getCurrentRoundStartRanking();
      if (atDepart?.length) {
        const departParts = atDepart.map((r) => {
          const short =
            scene.getPlayerDisplayInfo(r.playerIndex).driverName.split(' ').pop() ?? '?';
          const labeled = labelOwnDriver(short, scene.getPlayerDriverId(r.playerIndex));
          return `<span>${r.rank}. ${escapeHtml(labeled)}</span>`;
        });
        this.rankingRoundStart.innerHTML = `<span class="play-race-roll-order-label">Au départ du tour</span> ${departParts.join(' · ')}`;
        this.rankingRoundStart.hidden = false;
      } else {
        this.rankingRoundStart.hidden = true;
        this.rankingRoundStart.innerHTML = '';
      }
    } else {
      this.rankingRollOrder.hidden = true;
      this.rankingRollOrder.innerHTML = '';
      this.rankingRoundStart.hidden = true;
      this.rankingRoundStart.innerHTML = '';
    }

    this.rankingList.querySelectorAll<HTMLElement>('.play-race-rank-row').forEach((row) => {
      const idx = parseInt(row.dataset.playerIndex ?? '-1', 10);
      if (idx < 0) return;
      const showTip = (e: Event): void => {
        this.rankingTooltip.innerHTML = scene.buildPlayerTooltipHtml(idx);
        openTooltipFade(this.rankingTooltip);
        const rect = row.getBoundingClientRect();
        const layerRect = this.layer.getBoundingClientRect();
        this.rankingTooltip.style.left = `${rect.left - layerRect.left + rect.width / 2}px`;
        this.rankingTooltip.style.top = `${rect.top - layerRect.top - 6}px`;
        this.rankingTooltip.style.transform = 'translate(-50%, -100%)';
        void e;
      };
      const hideTip = (): void => {
        closeTooltipFade(this.rankingTooltip);
      };
      row.addEventListener('mouseenter', showTip);
      row.addEventListener('focus', showTip);
      row.addEventListener('mouseleave', hideTip);
      row.addEventListener('blur', hideTip);
    });

    this.historyTurnLabel.textContent = totalTurns > 0 ? `Tour ${selectedTurn}` : 'Tour —';
    this.historyPrevBtn.disabled = totalTurns <= 1 || selectedTurn <= 1;
    this.historyNextBtn.disabled = totalTurns <= 1 || selectedTurn >= totalTurns;
    this.historyPrevBtn.onclick = () => {
      scene.shiftSelectedHistoryTurn(-1);
      this.updateRanking(scene);
    };
    this.historyNextBtn.onclick = () => {
      scene.shiftSelectedHistoryTurn(1);
      this.updateRanking(scene);
    };

    if (!currentEntry) {
      this.historyList.innerHTML = `<div class="play-race-history-empty">Lancez le dé pour démarrer l'historique.</div>`;
      return;
    }

    const rows = turnHistoryRowsInRollOrder({
      rows: currentEntry.rows,
      turnOrderAtRoundStart: currentEntry.turnOrderAtRoundStart,
    });
    this.historyList.innerHTML = rows
      .map((row) => {
        const rollSlot = row.rollOrder ?? row.rank;
        const departRank =
          row.rank !== rollSlot
            ? `<span class="play-race-history-depart-rank" title="Classement en piste au début du tour">${row.rank}e au départ</span>`
            : '';
        return `<div class="play-race-history-row">
          <span class="play-race-history-pos" title="Ordre de lancer">${rollSlot}→</span>
          <span class="play-race-history-name">${escapeHtml(labelOwnDriver(row.driverName, scene.getPlayerDriverId(row.playerIndex)))}${departRank}</span>
          <span class="play-race-history-roll">${formatTurnHistoryRollMarkup(row)}</span>
        </div>`;
      })
      .join('');
  }

  private toggleRankingCollapsed(): void {
    this.collapsed = !this.collapsed;
    this.rankingPanel.classList.toggle('is-collapsed', this.collapsed);
    const btn = this.rankingPanel.querySelector('.play-race-float-collapse');
    if (btn) btn.textContent = this.collapsed ? '+' : '−';
  }

  destroy(): void {
    this.layer.remove();
  }
}
